import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { GamificationProvider } from './contexts/GamificationContext';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import LockScreen from './components/common/LockScreen';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import CandidateDashboard from './pages/candidate/Dashboard';
import CandidateProfile from './pages/candidate/Profile';
import CompanyDashboard from './pages/company/Dashboard';
import CompanyProfile from './pages/company/Profile';
import HostDashboard from './pages/host/Dashboard';
import HostEvents from './pages/host/Events';
import HostCompanies from './pages/host/Companies';
import SuperAdminDashboard from './pages/superadmin/Dashboard';
import './index.css';

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={user ? <Navigate to={`/${user.role}`} /> : <Login />} />
        <Route path="/register" element={user ? <Navigate to={`/${user.role}`} /> : <Register />} />
        
        {/* Candidate Routes */}
        <Route 
          path="/candidate" 
          element={
            <ProtectedRoute allowedRoles={['candidate']}>
              <CandidateDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/candidate/profile" 
          element={
            <ProtectedRoute allowedRoles={['candidate']}>
              <CandidateProfile />
            </ProtectedRoute>
          } 
        />

        {/* Company Routes */}
        <Route 
          path="/company" 
          element={
            <ProtectedRoute allowedRoles={['company']}>
              <CompanyDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/company/profile" 
          element={
            <ProtectedRoute allowedRoles={['company']}>
              <CompanyProfile />
            </ProtectedRoute>
          } 
        />

        {/* Host Routes (formerly Admin) */}
        <Route 
          path="/host" 
          element={
            <ProtectedRoute allowedRoles={['host']}>
              <HostDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/host/events" 
          element={
            <ProtectedRoute allowedRoles={['host']}>
              <HostEvents />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/host/companies" 
          element={
            <ProtectedRoute allowedRoles={['host']}>
              <HostCompanies />
            </ProtectedRoute>
          } 
        />

        {/* Super Admin Routes */}
        <Route 
          path="/super_admin" 
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } 
        />
      </Routes>
    </Layout>
  );
}

function App() {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [checkingLock, setCheckingLock] = useState(true);

  useEffect(() => {
    // Check if previously unlocked in this session
    const unlocked = sessionStorage.getItem('site_unlocked') === 'true';
    setIsUnlocked(unlocked);
    setCheckingLock(false);
  }, []);

  const handleUnlock = (password) => {
    if (password === 'bigfatboss') {
      setIsUnlocked(true);
      sessionStorage.setItem('site_unlocked', 'true');
      return true;
    }
    return false;
  };

  if (checkingLock) {
    return null; // Or a loading spinner
  }

  if (!isUnlocked) {
    return <LockScreen onUnlock={handleUnlock} />;
  }

  return (
    <Router>
      <AuthProvider>
        <DataProvider>
          <GamificationProvider>
            <AppRoutes />
          </GamificationProvider>
        </DataProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;